import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLogin from './AdminLogin';

const API = process.env.REACT_APP_API_URL || '';
const adm = (path, opts = {}) =>
  axios({ url: `${API}/admin${path}`, withCredentials: true, ...opts });

const STATUS_COLORS = {
  done: '#22c55e',
  running: '#6366f1',
  queued: '#f59e0b',
  failed: '#ef4444',
};

const S = {
  page: {
    minHeight: '100vh', background: '#0d0d1a', color: '#f0f0f5',
    fontFamily: 'Tajawal, sans-serif', fontSize: '14px',
  },
  header: {
    background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', height: '56px',
  },
  tab: (active) => ({
    padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
    background: active ? 'rgba(99,102,241,0.18)' : 'transparent',
    color: active ? '#818cf8' : 'rgba(255,255,255,0.5)',
    border: 'none', fontSize: '13px',
  }),
  btn: (color = '#6366f1', sm = false) => ({
    padding: sm ? '5px 12px' : '8px 16px', borderRadius: '6px', border: 'none',
    background: color, color: '#fff', fontWeight: 700,
    fontSize: sm ? '12px' : '13px', cursor: 'pointer',
  }),
  card: {
    background: '#13131f', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px', overflow: 'hidden',
  },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700,
    color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  td: (clickable) => ({
    padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)',
    cursor: clickable ? 'pointer' : 'default', verticalAlign: 'middle',
  }),
  badge: (status) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
    fontSize: '11px', fontWeight: 700,
    background: (STATUS_COLORS[status] || '#888') + '22',
    color: STATUS_COLORS[status] || '#888',
    border: `1px solid ${(STATUS_COLORS[status] || '#888')}44`,
  }),
  drawer: {
    position: 'fixed', top: 0, right: 0, width: '520px', height: '100vh',
    background: '#13131f', borderLeft: '1px solid rgba(255,255,255,0.1)',
    zIndex: 1000, display: 'flex', flexDirection: 'column', overflowY: 'auto',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999,
  },
  log: {
    background: '#0a0a14', borderRadius: '8px', padding: '12px',
    fontSize: '11px', lineHeight: 1.6, color: '#a0aec0', overflowX: 'auto',
    whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '340px', overflowY: 'auto',
    fontFamily: 'monospace',
  },
  select: {
    background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '6px', color: '#f0f0f5', padding: '6px 10px', fontSize: '13px',
  },
  input: {
    background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '6px', color: '#f0f0f5', padding: '6px 10px', fontSize: '13px',
    width: '180px',
  },
};

// ── Jobs Tab ──────────────────────────────────────────────────────────────────

function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState('');

  const PAGE_SIZE = 50;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusF) params.status = statusF;
      if (typeF) params.job_type = typeF;
      const r = await adm('/jobs', { params });
      setJobs(r.data.items);
      setTotal(r.data.total);
    } catch {
      /* 401 handled by parent */
    } finally {
      setLoading(false);
    }
  }, [page, statusF, typeF]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openDetail = async (job) => {
    setSelected(job);
    setDetail(null);
    setRetryMsg('');
    setDetailLoad(true);
    try {
      const r = await adm(`/jobs/${job.id}`);
      setDetail(r.data);
    } finally {
      setDetailLoad(false);
    }
  };

  const retry = async () => {
    if (!detail) return;
    setRetrying(true);
    setRetryMsg('');
    try {
      const r = await adm(`/jobs/${detail.id}/retry`, { method: 'POST' });
      setRetryMsg(`New job created: #${r.data.new_job_id}`);
      fetchJobs();
    } catch (e) {
      setRetryMsg(`Error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRetrying(false);
    }
  };

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ padding: '24px' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <select style={S.select} value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {['running', 'queued', 'done', 'failed'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={S.select} value={typeF} onChange={e => { setTypeF(e.target.value); setPage(1); }}>
          <option value="">All types</option>
          {['merge_word', 'merge_pdf', 'pptx_to_pdf', 'article_latex_to_word', 'latex_to_word'].map(t =>
            <option key={t} value={t}>{t}</option>)}
        </select>
        <button style={S.btn('#6366f1', true)} onClick={fetchJobs}>Refresh</button>
        <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.35)', fontSize: '12px' }}>
          {total} job{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['ID', 'Type', 'Status', 'Created', 'Finished', 'Error'].map(h =>
                <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading…</td></tr>
            ) : jobs.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No jobs</td></tr>
            ) : jobs.map(j => (
              <tr key={j.id} onClick={() => openDetail(j)}
                style={{ background: selected?.id === j.id ? 'rgba(99,102,241,0.08)' : 'transparent' }}>
                <td style={S.td(true)}><span style={{ color: '#818cf8' }}>#{j.id}</span></td>
                <td style={S.td(true)}>{j.job_type}</td>
                <td style={S.td(true)}><span style={S.badge(j.status)}>{j.status}</span></td>
                <td style={S.td(true)}>{j.created_at ? new Date(j.created_at).toLocaleString() : '—'}</td>
                <td style={S.td(true)}>{j.finished_at ? new Date(j.finished_at).toLocaleString() : '—'}</td>
                <td style={{ ...S.td(true), color: '#ef4444', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {j.error ? j.error.slice(0, 60) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'center' }}>
          <button style={S.btn('rgba(99,102,241,0.3)', true)} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
          <span style={{ padding: '5px 12px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
            {page} / {pages}
          </span>
          <button style={S.btn('rgba(99,102,241,0.3)', true)} onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>Next</button>
        </div>
      )}

      {/* Detail Drawer */}
      {selected && (
        <>
          <div style={S.overlay} onClick={() => { setSelected(null); setDetail(null); }} />
          <div style={S.drawer}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '16px' }}>Job #{selected.id}</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '2px' }}>{selected.job_type}</div>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>

            {detailLoad ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading…</div>
            ) : detail ? (
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Status row */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={S.badge(detail.status)}>{detail.status}</span>
                  {detail.retryable && (
                    <button style={S.btn('#6366f1', true)} onClick={retry} disabled={retrying}>
                      {retrying ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                  {retryMsg && (
                    <span style={{ fontSize: '12px', color: retryMsg.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
                      {retryMsg}
                    </span>
                  )}
                </div>

                {/* Timestamps */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    ['Created', detail.created_at],
                    ['Finished', detail.finished_at],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>{label}</div>
                      <div style={{ fontSize: '12px' }}>{val ? new Date(val).toLocaleString() : '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Params */}
                {detail.params && Object.keys(detail.params).length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Params</div>
                    <pre style={S.log}>{JSON.stringify(detail.params, null, 2)}</pre>
                  </div>
                )}

                {/* Error */}
                {detail.error && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Error</div>
                    <pre style={{ ...S.log, background: 'rgba(239,68,68,0.06)', color: '#fca5a5' }}>{detail.error}</pre>
                  </div>
                )}

                {/* Log */}
                <div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Log {!detail.has_log && <span style={{ color: '#f59e0b' }}>(no log file)</span>}
                  </div>
                  <pre style={S.log}>{detail.log || '(empty)'}</pre>
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

// ── Profiles Tab ──────────────────────────────────────────────────────────────

function ProfilesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adm('/profiles').then(r => setData(r.data.profiles)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading…</div>;

  return (
    <div style={{ padding: '24px' }}>
      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Publisher', 'Default BST', 'Template Files', 'Author-Year'].map(h =>
                <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data || []).map(p => (
              <tr key={p.publisher}>
                <td style={S.td(false)} title={p.publisher}><strong>{p.publisher}</strong></td>
                <td style={S.td(false)}><code style={{ fontSize: '11px', color: '#a5b4fc' }}>{p.default_bst}</code></td>
                <td style={S.td(false)}>
                  {p.template_files.length === 0
                    ? <span style={{ color: 'rgba(255,255,255,0.3)' }}>none</span>
                    : p.template_files.join(', ')}
                </td>
                <td style={S.td(false)}>
                  <span style={S.badge(p.author_year ? 'done' : 'failed')}>
                    {p.author_year ? 'yes' : 'no'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Models Tab ────────────────────────────────────────────────────────────────

function ModelsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adm('/models').then(r => setData(r.data.models)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading…</div>;

  const statusColor = (s) => {
    if (s === 'loaded' || s === 'available') return 'done';
    if (s === 'not_loaded' || s === 'not_installed') return 'queued';
    return 'failed';
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Model', 'Purpose', 'Languages', 'Status', 'Version'].map(h =>
                <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data || []).map((m, i) => (
              <tr key={i}>
                <td style={S.td(false)}><strong>{m.name}</strong></td>
                <td style={S.td(false)}>{m.purpose || '—'}</td>
                <td style={S.td(false)}>{m.languages || '—'}</td>
                <td style={S.td(false)}>
                  <span style={S.badge(statusColor(m.status))}>{m.status}</span>
                </td>
                <td style={S.td(false)}>
                  {m.error
                    ? <span style={{ color: '#ef4444', fontSize: '11px' }}>{m.error.slice(0, 60)}</span>
                    : (m.version || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────────────

function StatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    adm('/stats')
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading…</div>;
  if (err) return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Error: {err}</div>;

  const maxCount = Math.max(...(data.by_endpoint.map(e => e.count)), 1);
  const BAR_MAX_W = 200;

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Today', value: data.total_today },
          { label: 'This Week', value: data.total_this_week },
          { label: 'All Time', value: data.total_all_time },
        ].map(({ label, value }) => (
          <div key={label} style={{
            ...S.card, padding: '20px 28px', flex: 1, textAlign: 'center',
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#818cf8' }}>{value ?? 0}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* By endpoint */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Conversions by type</div>
        <div style={S.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={S.th}>Endpoint</th>
                <th style={S.th}>Count</th>
                <th style={S.th}>Bar</th>
              </tr>
            </thead>
            <tbody>
              {data.by_endpoint.length === 0 ? (
                <tr><td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No data yet</td></tr>
              ) : data.by_endpoint.map(({ endpoint, count }) => (
                <tr key={endpoint}>
                  <td style={S.td(false)}><code style={{ color: '#a5b4fc', fontSize: '12px' }}>{endpoint}</code></td>
                  <td style={{ ...S.td(false), fontWeight: 700, color: '#f0f0f5' }}>{count}</td>
                  <td style={S.td(false)}>
                    <div style={{
                      height: '10px', borderRadius: '4px', background: '#6366f1',
                      width: `${Math.round((count / maxCount) * BAR_MAX_W)}px`,
                      minWidth: '4px',
                    }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily last 30 days */}
      <div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Daily — last 30 days</div>
        <div style={S.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Count</th>
                <th style={S.th}>Bar</th>
              </tr>
            </thead>
            <tbody>
              {data.daily_last_30_days.length === 0 ? (
                <tr><td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No data yet</td></tr>
              ) : (() => {
                const maxD = Math.max(...data.daily_last_30_days.map(d => d.count), 1);
                return [...data.daily_last_30_days].reverse().map(({ date, count }) => (
                  <tr key={date}>
                    <td style={S.td(false)}>{date}</td>
                    <td style={{ ...S.td(false), fontWeight: 700, color: '#f0f0f5' }}>{count}</td>
                    <td style={S.td(false)}>
                      <div style={{
                        height: '10px', borderRadius: '4px', background: '#22c55e',
                        width: `${Math.round((count / maxD) * BAR_MAX_W)}px`,
                        minWidth: '4px',
                      }} />
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Admin Shell ───────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true=ok, false=needs login
  const [tab, setTab] = useState('jobs');
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    adm('/jobs', { params: { page: 1, page_size: 1 } })
      .then(() => setAuthed(true))
      .catch(e => {
        if (e.response?.status === 401) setAuthed(false);
        else setAuthed(true); // network error — still show shell, let tabs handle it
      });
  }, []);

  const logout = async () => {
    setLoggingOut(true);
    try { await adm('/logout', { method: 'POST' }); } catch { }
    setAuthed(false);
    setLoggingOut(false);
  };

  if (authed === null) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>Checking session…</span>
      </div>
    );
  }

  if (authed === false) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  const TABS = [
    { id: 'jobs', label: 'Jobs' },
    { id: 'profiles', label: 'Profiles' },
    { id: 'models', label: 'Models' },
    { id: 'stats', label: 'Stats' },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🛡️</span>
          <span style={{ fontWeight: 700, fontSize: '15px', color: '#f0f0f5' }}>Admin Panel</span>
          <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 4px' }}>·</span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>Smart Converter</span>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {TABS.map(t => (
            <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 8px' }} />
          <button style={S.btn('#374151', true)} onClick={logout} disabled={loggingOut}>
            {loggingOut ? '…' : 'Logout'}
          </button>
        </div>
      </div>

      {tab === 'jobs' && <JobsTab />}
      {tab === 'profiles' && <ProfilesTab />}
      {tab === 'models' && <ModelsTab />}
      {tab === 'stats' && <StatsTab />}
    </div>
  );
}
